import { useCallback, useMemo, useSyncExternalStore } from "react";

/**
 * External store for row-level inline editing in tables.
 *
 * Tracks three pieces of UI state:
 *   - which row (if any) is currently being edited
 *   - which row (if any) is currently in flight to the backend
 *   - the per-row uncontrolled-input draft values
 *
 * Why not useState?
 *   Inline editing keystrokes happen many times per second; if drafts
 *   lived in React state, every keypress would re-render the entire
 *   table parent (rebuilding columns, walking every row). Moving them
 *   to an external ref-based store isolates the update path so only
 *   the editing cell's row commits visible work.
 *
 *   The same pattern is used for `useHeaderMenuStore` — see that file
 *   for the broader rationale.
 */
export const createInlineEditStore = () => {
  let editingRowKey = null;
  let savingRowKey = null;
  let drafts = new Map();
  const listeners = new Set();
  const notify = () => listeners.forEach((listener) => listener());

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getEditingRowKey: () => editingRowKey,
    getSavingRowKey: () => savingRowKey,
    getDraft: (rowKey) => drafts.get(rowKey) ?? null,
    getDraftValue: (rowKey, field) => drafts.get(rowKey)?.[field],
    beginEdit: (rowKey, initialDraft) => {
      const nextRowKey = rowKey ?? null;
      const prevEditingKey = editingRowKey;
      editingRowKey = nextRowKey;
      savingRowKey = null;
      drafts = new Map(drafts);
      drafts.set(nextRowKey, { ...(initialDraft ?? {}) });
      if (prevEditingKey && prevEditingKey !== nextRowKey) drafts.delete(prevEditingKey);
      notify();
    },
    updateDraft: (rowKey, field, value) => {
      if (!rowKey) return;
      const current = drafts.get(rowKey) ?? {};
      if (current[field] === value) return;
      current[field] = value;
      drafts.set(rowKey, current);
    },
    cancelEdit: () => {
      const hadState = editingRowKey !== null || savingRowKey !== null || drafts.size > 0;
      if (!hadState) return;
      if (editingRowKey !== null) drafts.delete(editingRowKey);
      editingRowKey = null;
      savingRowKey = null;
      notify();
    },
    clear: () => {
      if (editingRowKey === null && savingRowKey === null && drafts.size === 0) return;
      editingRowKey = null;
      savingRowKey = null;
      drafts = new Map();
      notify();
    },
    startSaving: (rowKey) => {
      if (!rowKey) return false;
      if (savingRowKey !== null) return false;
      savingRowKey = rowKey;
      notify();
      return true;
    },
    stopSaving: () => {
      if (savingRowKey === null) return;
      savingRowKey = null;
      notify();
    },
    completeSave: (rowKey) => {
      const hadState = editingRowKey !== null || savingRowKey !== null || drafts.size > 0;
      if (!hadState) return;
      if (rowKey) drafts.delete(rowKey);
      editingRowKey = null;
      savingRowKey = null;
      notify();
    },
  };
};

/**
 * Hook factory: returns a stable store instance for the lifetime of
 * the component. Pair with the per-cell selector hooks below.
 */
export const useInlineEditStore = () => useMemo(() => createInlineEditStore(), []);

/**
 * Per-row selector — only re-renders when THIS row toggles between
 * editing / not-editing.
 */
export const useIsInlineRowEditing = (store, rowKey) => {
  const getSnapshot = useCallback(() => store.getEditingRowKey() === rowKey, [store, rowKey]);
  return useSyncExternalStore(store.subscribe, getSnapshot);
};

/**
 * Returns the row key currently being saved (or null). Used to disable
 * other rows' Edit buttons while a save is in flight.
 */
export const useInlineSavingRowKey = (store) => {
  const getSnapshot = useCallback(() => store.getSavingRowKey(), [store]);
  return useSyncExternalStore(store.subscribe, getSnapshot);
};
